import React from "react";
import { ShieldCheck, Gauge, FileText, AlertTriangle, RefreshCw, Activity } from "lucide-react";
import SectionHeading from "./SectionHeading";

const qualityPoints = [
  {
    icon: <ShieldCheck size={22} />,
    title: "Bank-Grade Security",
    description:
      "Comprehensive encryption (at rest and in transit), vulnerability testing, and OWASP compliance to keep systems protected against threats.",
    badge: "Security",
    badgeColor: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: <Gauge size={22} />,
    title: "Performance Optimization",
    description:
      "Sub-second API response times through intelligent query caching, CDN integration, and optimized payload compression.",
    badge: "Speed & Scale",
    badgeColor: "bg-blue-50 text-blue-600",
  },
  {
    icon: <FileText size={22} />,
    title: "Interactive API Docs & Testing",
    description:
      "Swagger/OpenAPI documentation and automated Postman/Jest test suites ensuring ease of developer consumption and reliability.",
    badge: "Developer Experience",
    badgeColor: "bg-purple-50 text-purple-600",
  },
  {
    icon: <AlertTriangle size={22} />,
    title: "Logging & Error Diagnostics",
    description:
      "Centralized log aggregations (Sentry, Datadog) and structured exception handling for instant troubleshooting and high uptime.",
    badge: "Monitoring",
    badgeColor: "bg-amber-50 text-amber-600",
  },
  {
    icon: <RefreshCw size={22} />,
    title: "Legacy System Integration",
    description:
      "Modernize legacy platforms by wrapping existing databases and monolithic logic with modern REST/GraphQL micro-frontends.",
    badge: "Modernization",
    badgeColor: "bg-indigo-50 text-indigo-600",
  },
  {
    icon: <Activity size={22} />,
    title: "99.9% Uptime Guarantee",
    description:
      "Health-check endpoints, load balancing, and failover clustering to ensure continuous business operations around the clock.",
    badge: "Reliability",
    badgeColor: "bg-cyan-50 text-cyan-600",
  },
];

export default function WebServiceEnterpriseQuality() {
  return (
    <section className="bg-[#F8F8FB] py-20 px-6 border-t border-gray-200/60">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mx-auto">
          <SectionHeading
            eyebrow="Quality & Reliability"
            title="Security, Speed & Enterprise Standards"
            description="We leave no detail unattended. From meticulous API documentation and error diagnostics to enterprise security and legacy modernization."
            theme="light"
            titleClassName="text-[#111827]"
            descriptionClassName="text-[16px] leading-8"
          />
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
          {qualityPoints.map((item, index) => (
            <div
              key={index}
              className="bg-white border border-[#ECECEC] rounded-2xl p-8 hover:-translate-y-2 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#EBF2FF] flex items-center justify-center text-[#355dff]">
                    {item.icon}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${item.badgeColor}`}>
                    {item.badge}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-[14px] leading-7">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
